import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Chip from 'tio-reimagined/components/chip';
import Text from 'tio-reimagined/components/text';
import PageHero from 'tio-reimagined/components/page-hero';
import SectionHeader from 'tio-reimagined/components/section-header';
import Button from 'tio-reimagined/components/button';
import TooltipButton from 'tio-reimagined/components/tooltip-button';
import {
  PhArrowLeft,
  PhCursorClick,
  PhFloppyDisk,
  PhTrash,
  PhPencilSimple,
  PhPlus,
  PhDownloadSimple,
  PhShareNetwork,
  PhArrowRight,
  PhCheck,
  PhWarning,
  PhInfo,
  PhRocket,
  PhLightning,
  PhHeart,
  PhStar,
  PhChat,
  PhChatCircle,
  PhGear,
  PhBell,
  PhEye,
  PhCopy,
  PhArrowsCounterClockwise,
  PhHandPointing,
} from 'ember-phosphor-icons';

// ---------------------------------------------------------------------------
// Buttons Demo Page
// ---------------------------------------------------------------------------

export default class ButtonsDetailPage extends Component {
  // Simulate async actions
  @tracked isPrimarySaving = false;
  @tracked isDangerDeleting = false;
  @tracked isSuccessSubmitting = false;
  @tracked isDefaultLoading = false;

  // TooltipButton active state demo
  @tracked isCommentsOpen = false;
  @tracked isSettingsOpen = false;
  @tracked tooltipDisabled = false;

  toggleComments = () => {
    this.isCommentsOpen = !this.isCommentsOpen;
  };

  toggleSettings = () => {
    this.isSettingsOpen = !this.isSettingsOpen;
  };

  toggleTooltipDisabled = () => {
    this.tooltipDisabled = !this.tooltipDisabled;
  };

  simulatePrimarySave = () => {
    this.isPrimarySaving = true;
    setTimeout(() => {
      this.isPrimarySaving = false;
    }, 2500);
  };

  simulateDangerDelete = () => {
    this.isDangerDeleting = true;
    setTimeout(() => {
      this.isDangerDeleting = false;
    }, 2000);
  };

  simulateSuccessSubmit = () => {
    this.isSuccessSubmitting = true;
    setTimeout(() => {
      this.isSuccessSubmitting = false;
    }, 2000);
  };

  simulateDefaultLoad = () => {
    this.isDefaultLoading = true;
    setTimeout(() => {
      this.isDefaultLoading = false;
    }, 2000);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.components"
          class="inline-flex items-center gap-2 text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Components
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero
        @title="Button"
        @subtitle="Zero-Frontile button with polished transitions, three-dot loading, and full intent/appearance/size matrix."
        @icon={{PhCursorClick}}
        @theme="violet"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">
            Reimagined
          </Chip>
          <Chip @intent="success">
            Zero Dependency
          </Chip>
          <Chip @intent="warning">
            Accessible
          </Chip>
        </:actions>
      </PageHero>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {{! ====== Intents — Filled ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhCursorClick}}
            @title="Intents (Filled)"
            @subtitle="Primary, danger, warning, success, and default"
            @iconClass="text-blue-500"
          />

          <div class="flex flex-wrap items-center gap-3">
            <Button @intent="primary">Primary</Button>
            <Button @intent="danger">Danger</Button>
            <Button @intent="warning">Warning</Button>
            <Button @intent="success">Success</Button>
            <Button>Default</Button>
          </div>

          <div class="text-xs text-gray-500 font-mono mt-2">
            Primary uses brand color #006c9a
          </div>
        </div>

        {{! ====== Intents — Outlined ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhCursorClick}}
            @title="Intents (Outlined)"
            @subtitle="Border + transparent background variant"
            @iconClass="text-violet-500"
          />

          <div class="flex flex-wrap items-center gap-3">
            <Button @intent="primary" @appearance="outlined">Primary</Button>
            <Button @intent="danger" @appearance="outlined">Danger</Button>
            <Button @intent="warning" @appearance="outlined">Warning</Button>
            <Button @intent="success" @appearance="outlined">Success</Button>
            <Button @appearance="outlined">Default</Button>
          </div>
        </div>

        {{! ====== Intents — Minimal ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhCursorClick}}
            @title="Intents (Minimal)"
            @subtitle="Text-only, no background or border"
            @iconClass="text-emerald-500"
          />

          <div class="flex flex-wrap items-center gap-3">
            <Button @intent="primary" @appearance="minimal">Primary</Button>
            <Button @intent="danger" @appearance="minimal">Danger</Button>
            <Button @intent="warning" @appearance="minimal">Warning</Button>
            <Button @intent="success" @appearance="minimal">Success</Button>
            <Button @appearance="minimal">Default</Button>
          </div>
        </div>

        {{! ====== Sizes ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhCursorClick}}
            @title="Sizes"
            @subtitle="Small, medium (default), and large"
            @iconClass="text-amber-500"
          />

          <div class="flex flex-wrap items-end gap-3">
            <Button @intent="primary" @size="sm">Small</Button>
            <Button @intent="primary" @size="md">Medium</Button>
            <Button @intent="primary" @size="lg">Large</Button>
          </div>

          <div class="flex flex-wrap items-end gap-3 mt-3">
            <Button @intent="primary" @appearance="outlined" @size="sm">Small</Button>
            <Button @intent="primary" @appearance="outlined" @size="md">Medium</Button>
            <Button @intent="primary" @appearance="outlined" @size="lg">Large</Button>
          </div>
        </div>

        {{! ====== Icons ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhStar}}
            @title="With Icons"
            @subtitle="Leading, trailing, and icon-only buttons"
            @iconClass="text-cyan-500"
          />

          <div class="space-y-3">
            <Text @size="sm" @variant="muted">Leading icon (default)</Text>
            <div class="flex flex-wrap items-center gap-3">
              <Button @intent="primary" @icon={{PhFloppyDisk}} @label="Save" />
              <Button @intent="danger" @icon={{PhTrash}} @label="Delete" />
              <Button @intent="success" @icon={{PhCheck}} @label="Approve" />
              <Button @appearance="outlined" @icon={{PhPencilSimple}} @label="Edit" />
            </div>
          </div>

          <div class="space-y-3">
            <Text @size="sm" @variant="muted">Trailing icon</Text>
            <div class="flex flex-wrap items-center gap-3">
              <Button
                @intent="primary"
                @icon={{PhArrowRight}}
                @label="Continue"
                @iconPlacement="end"
              />
              <Button
                @appearance="outlined"
                @icon={{PhDownloadSimple}}
                @label="Download"
                @iconPlacement="end"
              />
            </div>
          </div>

          <div class="space-y-3">
            <Text @size="sm" @variant="muted">Icon only</Text>
            <div class="flex flex-wrap items-center gap-3">
              <Button @intent="primary" @icon={{PhPlus}} />
              <Button @appearance="outlined" @icon={{PhShareNetwork}} />
              <Button @appearance="minimal" @icon={{PhHeart}} @intent="danger" />
              <Button @appearance="minimal" @icon={{PhStar}} @intent="warning" />
            </div>
          </div>
        </div>

        {{! ====== Loading States ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhLightning}}
            @title="Loading States"
            @subtitle="Click to trigger — three-dot pulse animation"
            @iconClass="text-rose-500"
          />

          <div class="space-y-4">
            <div class="flex flex-wrap items-center gap-3">
              <Button
                @intent="primary"
                @isRunning={{this.isPrimarySaving}}
                @icon={{PhFloppyDisk}}
                @label="Save Changes"
                {{on "click" this.simulatePrimarySave}}
              />
              <Button
                @intent="danger"
                @isRunning={{this.isDangerDeleting}}
                @icon={{PhTrash}}
                @label="Delete"
                {{on "click" this.simulateDangerDelete}}
              />
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <Button
                @intent="success"
                @isRunning={{this.isSuccessSubmitting}}
                @label="Submit"
                {{on "click" this.simulateSuccessSubmit}}
              />
              <Button
                @appearance="outlined"
                @isRunning={{this.isDefaultLoading}}
                @label="Load More"
                {{on "click" this.simulateDefaultLoad}}
              />
            </div>
            <Text @size="sm" @variant="muted">
              Always-on preview:
            </Text>
            <div class="flex flex-wrap items-center gap-3">
              <Button @intent="primary" @isRunning={{true}}>Saving…</Button>
              <Button @intent="danger" @appearance="outlined" @isRunning={{true}}>Deleting…</Button>
              <Button
                @intent="success"
                @appearance="minimal"
                @isRunning={{true}}
              >Submitting…</Button>
            </div>
          </div>
        </div>

        {{! ====== Disabled ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhInfo}}
            @title="Disabled States"
            @subtitle="Reduced opacity + cursor-not-allowed"
            @iconClass="text-gray-500"
          />

          <div class="flex flex-wrap items-center gap-3">
            <Button @intent="primary" @isDisabled={{true}}>Primary</Button>
            <Button @intent="danger" @isDisabled={{true}}>Danger</Button>
            <Button @appearance="outlined" @isDisabled={{true}}>Outlined</Button>
            <Button @appearance="minimal" @isDisabled={{true}}>Minimal</Button>
          </div>
        </div>

        {{! ====== Full Width ====== }}
        <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5">
          <SectionHeader
            @icon={{PhRocket}}
            @title="Full Width"
            @subtitle="Stretches to fill container"
            @iconClass="text-indigo-500"
          />

          <Button
            @intent="primary"
            @isFullWidth={{true}}
            @icon={{PhRocket}}
            @label="Get Started"
            @size="lg"
          />
          <Button
            @intent="success"
            @appearance="outlined"
            @isFullWidth={{true}}
            @label="Confirm Selection"
          />
        </div>

        {{! ====== Button Group Pattern ====== }}
        <div
          class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5 lg:col-span-2"
        >
          <SectionHeader
            @icon={{PhWarning}}
            @title="Composition Patterns"
            @subtitle="Common button group layouts you'll use in forms, modals, and toolbars"
            @iconClass="text-fuchsia-500"
          />

          <div class="space-y-6">
            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Form actions (right-aligned)</Text>
              <div class="flex justify-end gap-3">
                <Button @appearance="outlined">Cancel</Button>
                <Button @intent="primary" @icon={{PhCheck}} @label="Save" />
              </div>
            </div>

            <hr class="border-gray-200" />

            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Destructive confirmation</Text>
              <div class="flex justify-end gap-3">
                <Button @appearance="minimal">Keep</Button>
                <Button @intent="danger" @icon={{PhTrash}} @label="Delete Forever" />
              </div>
            </div>

            <hr class="border-gray-200" />

            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Toolbar (icon-only)</Text>
              <div
                class="inline-flex rounded-lg border border-gray-200 divide-x divide-gray-200 overflow-hidden"
              >
                <Button @appearance="minimal" @icon={{PhPencilSimple}} class="rounded-none" />
                <Button
                  @appearance="minimal"
                  @icon={{PhTrash}}
                  @intent="danger"
                  class="rounded-none"
                />
                <Button @appearance="minimal" @icon={{PhDownloadSimple}} class="rounded-none" />
                <Button @appearance="minimal" @icon={{PhShareNetwork}} class="rounded-none" />
              </div>
            </div>

            <hr class="border-gray-200" />

            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Mixed sizes in a row</Text>
              <div class="flex items-center gap-3">
                <Button @intent="primary" @size="lg" @icon={{PhRocket}} @label="Launch" />
                <Button @appearance="outlined" @size="md">Configure</Button>
                <Button @appearance="minimal" @size="sm">Skip</Button>
              </div>
            </div>
          </div>
        </div>

        {{! ====== TooltipButton — Basic ====== }}
        <div
          class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6 space-y-5 lg:col-span-2"
        >
          <SectionHeader
            @icon={{PhHandPointing}}
            @title="TooltipButton"
            @subtitle="Button with integrated hover tooltip — composes Button + Popover"
            @iconClass="text-violet-500"
          />

          <div class="space-y-6">
            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Icon-only with tooltips (common toolbar
                pattern)</Text>
              <div class="flex flex-wrap items-center gap-2">
                <TooltipButton
                  @tooltip="Save changes"
                  @icon={{PhFloppyDisk}}
                  @appearance="minimal"
                />
                <TooltipButton
                  @tooltip="Edit item"
                  @icon={{PhPencilSimple}}
                  @appearance="minimal"
                />
                <TooltipButton
                  @tooltip="Delete item"
                  @icon={{PhTrash}}
                  @appearance="minimal"
                  @intent="danger"
                />
                <TooltipButton
                  @tooltip="Copy to clipboard"
                  @icon={{PhCopy}}
                  @appearance="minimal"
                />
                <TooltipButton @tooltip="Share" @icon={{PhShareNetwork}} @appearance="minimal" />
                <TooltipButton
                  @tooltip="Download"
                  @icon={{PhDownloadSimple}}
                  @appearance="minimal"
                />
              </div>
            </div>

            <hr class="border-gray-200" />

            <div>
              <Text @size="sm" @variant="muted" class="mb-3">With label + tooltip for extra context</Text>
              <div class="flex flex-wrap items-center gap-3">
                <TooltipButton
                  @tooltip="Save your progress — you can continue editing later"
                  @icon={{PhFloppyDisk}}
                  @intent="primary"
                  @label="Save"
                />
                <TooltipButton
                  @tooltip="This action cannot be undone"
                  @icon={{PhTrash}}
                  @intent="danger"
                  @appearance="outlined"
                  @label="Delete"
                />
                <TooltipButton
                  @tooltip="Refresh data from the server"
                  @icon={{PhArrowsCounterClockwise}}
                  @appearance="outlined"
                  @label="Refresh"
                />
              </div>
            </div>

            <hr class="border-gray-200" />

            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Placement options</Text>
              <div class="flex flex-wrap items-center gap-3 py-8">
                <TooltipButton
                  @tooltip="Top (default)"
                  @tooltipPlacement="top"
                  @icon={{PhInfo}}
                  @appearance="outlined"
                />
                <TooltipButton
                  @tooltip="Bottom"
                  @tooltipPlacement="bottom"
                  @icon={{PhInfo}}
                  @appearance="outlined"
                />
                <TooltipButton
                  @tooltip="Left"
                  @tooltipPlacement="left"
                  @icon={{PhInfo}}
                  @appearance="outlined"
                />
                <TooltipButton
                  @tooltip="Right"
                  @tooltipPlacement="right"
                  @icon={{PhInfo}}
                  @appearance="outlined"
                />
              </div>
            </div>

            <hr class="border-gray-200" />

            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Active state (toggle — for drawer/panel
                triggers)</Text>
              <div class="flex flex-wrap items-center gap-2">
                <TooltipButton
                  @tooltip="Comments"
                  @icon={{PhChat}}
                  @appearance="minimal"
                  @isActive={{this.isCommentsOpen}}
                  {{on "click" this.toggleComments}}
                />
                <TooltipButton
                  @tooltip="Settings"
                  @icon={{PhGear}}
                  @appearance="minimal"
                  @isActive={{this.isSettingsOpen}}
                  {{on "click" this.toggleSettings}}
                />
                <TooltipButton @tooltip="Notifications" @icon={{PhBell}} @appearance="minimal" />
                <TooltipButton @tooltip="Preview" @icon={{PhEye}} @appearance="minimal" />
              </div>
              <Text @size="xs" @variant="muted" class="mt-2">
                Click Comments or Settings to toggle the active highlight.
              </Text>
            </div>

            <hr class="border-gray-200" />

            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Tooltip disabled</Text>
              <div class="flex items-center gap-3">
                <TooltipButton
                  @tooltip="This tooltip can be toggled"
                  @icon={{PhChatCircle}}
                  @appearance="minimal"
                  @tooltipDisabled={{this.tooltipDisabled}}
                />
                <Button @appearance="outlined" @size="sm" {{on "click" this.toggleTooltipDisabled}}>
                  {{if this.tooltipDisabled "Enable Tooltip" "Disable Tooltip"}}
                </Button>
              </div>
            </div>

            <hr class="border-gray-200" />

            <div>
              <Text @size="sm" @variant="muted" class="mb-3">Sizes</Text>
              <div class="flex flex-wrap items-end gap-3">
                <TooltipButton
                  @tooltip="Small button"
                  @icon={{PhStar}}
                  @size="sm"
                  @appearance="outlined"
                  @intent="primary"
                />
                <TooltipButton
                  @tooltip="Medium button (default)"
                  @icon={{PhStar}}
                  @size="md"
                  @appearance="outlined"
                  @intent="primary"
                />
                <TooltipButton
                  @tooltip="Large button"
                  @icon={{PhStar}}
                  @size="lg"
                  @appearance="outlined"
                  @intent="primary"
                />
              </div>
            </div>
          </div>
        </div>

      </div>
    </VStack>
  </template>
}
